import { useMemo } from 'react';
import { useAuthStore } from '../../store/useAuthStore';
import { useProfileStore } from '../../store/useProfileStore';
import { ROLE_LABEL, capabilitiesFor, can, missingReason, type Capability } from './capabilities';

/**
 * The signed-in admin: session status, company profile and what it may do.
 * Components gate on `can('payouts.write')`, never on role strings. A write
 * control the user can see but not use renders DISABLED, not hidden: pass
 * `lockedReason={locked('payouts.write')}` to the Button (undefined when allowed).
 */
export function useSession() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const status = useAuthStore((s) => s.sessionStatus);
  const profile = useProfileStore((s) => s.activeProfile);
  const capabilities = useMemo(() => capabilitiesFor(profile?.roles), [profile?.roles]);

  return {
    isAuthenticated,
    status,
    profile,
    capabilities,
    can: (capability: Capability) => can(capabilities, capability),
    /** Undefined when allowed; otherwise the tooltip for the disabled control. */
    locked: (capability: Capability) => (can(capabilities, capability) ? undefined : missingReason(capability)),
  };
}

/** Display label for the signed-in admin's roles, e.g. "Staff · Marketing". */
export function tierLabel(roles: readonly string[] | undefined): string {
  const names = (roles ?? []).map((r) => ROLE_LABEL[r]).filter(Boolean);
  return names.length ? names.join(' · ') : 'No admin access';
}
